import { mergeDeepRight, omit, sum } from 'ramda';

import {
  REGION,
  COMMON_THEMATIC,
  CLASSIFICATION,
  FINANCIAL_PUBLICATION,
  PUBLICATION,
  FILTER,
} from '../../../types/filters';
import { MEDIA_TYPE } from '../../../types/press-media';

type SearchFacet = {
  nbResults: number;
  values: Record<string, number>;
};

const initializeFacets = (
  facets: string[] | Record<string, string>
): SearchFacet =>
  Object.values(facets).reduce<{
    nbResults: number;
    values: Record<string, number>;
  }>(
    (acc, facet) => ({
      ...acc,
      values: { ...acc.values, [facet]: 0 },
    }),
    {
      nbResults: 0,
      values: {},
    }
  );

const AVAILABLE_FACETS: Record<string, SearchFacet> = {
  [FILTER.CLASSIFICATION]: initializeFacets(
    omit(
      [CLASSIFICATION.INNER_PAGE, CLASSIFICATION.PUBLICATION],
      CLASSIFICATION
    )
  ),
  [FILTER.MEDIA_TYPE]: initializeFacets([MEDIA_TYPE.RELEASE]),
  [FILTER.REGION]: initializeFacets(REGION),
  [FILTER.COMMON_THEMATIC]: initializeFacets(COMMON_THEMATIC),
  [FILTER.PUBLICATION]: initializeFacets(PUBLICATION),
  [FILTER.FINANCIAL_PUBLICATION]: initializeFacets(FINANCIAL_PUBLICATION),
};

/*
 * Modify algolia facets response to have all facets
 * with all available values even when 0 result.
 *
 * Algolia api return only facets and values with results.
 */
export const normalizeFacets = (
  results: {
    facets?: {
      [facetName: string]: { [facetValue: string]: number };
    };
  }[]
): Record<string, SearchFacet> =>
  results.reduce((acc, { facets = {} }) => {
    // Extract facets prop and remove 'filters." from keys from algolia results
    const normalizedFacets = Object.entries(facets).reduce(
      (accFacets, [key, value]) => {
        const newKey = key.replace(/^filters./, '');

        return {
          ...accFacets,
          [newKey]: { nbResults: sum(Object.values(value)), values: value },
        };
      },
      {}
    );

    return mergeDeepRight(acc, normalizedFacets);
  }, AVAILABLE_FACETS);
